import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { trackEvent } from '../analytics/posthog';
import { AppLoading } from '../marketing/app-loading';
import { CenteredShell, ShellHeading } from '../marketing/centered-shell';
import { PersonaChorus } from '../marketing/persona-chorus';
import { onboardingPathFor } from './redirect';
import { completeMagicLinkSignIn } from './service';
import { useAuth } from './use-auth';
import { useExtensionAuthHandoff } from './use-extension-auth-handoff';

// Both params are appended by apps/api's MagicLinkService when it builds the
// link, and validated there before it ever reaches an email — see
// SendMagicLinkDto. `redirect` is optional; falling back to "/" mirrors what
// Google sign-in already does.
function readLinkParams(searchParams: URLSearchParams) {
  return {
    email: searchParams.get('email'),
    redirect: searchParams.get('redirect') ?? '/',
  };
}

/**
 * "/login/verify-link" — where a clicked magic-link email lands. Firebase
 * encodes the actual sign-in proof in the URL itself (mode, oobCode, ...);
 * this page's only job is to hand that URL plus the email back to
 * completeMagicLinkSignIn and then behave exactly like a successful Google
 * sign-in: refresh the session query, replace-navigate onward.
 */
export function VerifyMagicLinkPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshSession } = useAuth();
  const handOffToExtension = useExtensionAuthHandoff();
  const [error, setError] = useState<string | null>(null);
  const hasStarted = useRef(false);

  useEffect(() => {
    // Effects run twice under StrictMode in dev — a magic link is single-use,
    // so a second call would just fail with a confusing error.
    if (hasStarted.current) {
      return;
    }
    hasStarted.current = true;

    const { email, redirect } = readLinkParams(searchParams);

    if (!email) {
      trackEvent('login_failed', {
        reason: 'link_missing_email',
        source: 'magic_link',
      });
      setError(
        'This link is missing its email address. Please request a new one.',
      );
      return;
    }

    void (async () => {
      try {
        const session = await completeMagicLinkSignIn(email);
        await refreshSession();
        trackEvent('login_completed', {
          new_user: session.activated === false,
          source: 'magic_link',
        });
        if (session.activated === false) {
          void navigate(onboardingPathFor(searchParams), { replace: true });
          return;
        }
        if (session.user) await handOffToExtension(session.user);
        void navigate(redirect, { replace: true });
      } catch (err) {
        trackEvent('login_failed', { source: 'magic_link' });
        setError(
          err instanceof Error
            ? err.message
            : 'Could not complete sign-in with this link.',
        );
      }
    })();
    // Deliberately runs once on mount: it's the completion of a single-use
    // link, not something that should re-run on navigation-hook re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The wait looks like every other wait in the app — the mark breathing on
  // the dotted ground — only it says what it is waiting for.
  if (!error) return <AppLoading label="Signing you in…" />;

  return (
    <CenteredShell width="max-w-md">
      <PersonaChorus />
      <ShellHeading sub={<span role="alert">{error}</span>}>
        Sign-in link problem
      </ShellHeading>
      <a
        className="mt-6 inline-block text-body font-semibold text-ink underline"
        href="/login"
      >
        Back to sign in
      </a>
    </CenteredShell>
  );
}
